import {
  ApiError,
  apiFetch,
  apiRequest,
  downloadFilename,
  saveBlobAsFile,
} from '../../services/apiClient';

// Bulk operations over a page-scoped selection of contract ids. Each returns
// { results: [{ id, ok, changed?, error? }], updated, failed } so callers can
// report partial failures (ids that no longer exist).

export function bulkUpdateStatus(ids, status) {
  return apiRequest('/contracts/bulk/status', {
    method: 'POST',
    json: { ids, status },
  });
}

export function bulkUpdateOwner(ids, owner) {
  return apiRequest('/contracts/bulk/owner', {
    method: 'POST',
    json: { ids, owner: owner || null },
  });
}

export function bulkUpdateTags(ids, { add = [], remove = [] } = {}) {
  return apiRequest('/contracts/bulk/tags', {
    method: 'POST',
    json: { ids, add, remove },
  });
}

// Bulk copy-of-record download (doc-119). One id → the file itself; several
// → one zip. Contracts without a document never fail the batch — their ids
// come back in skippedIds (a response header on file responses; the JSON
// body when nothing at all was downloadable) for the caller to report.
export async function bulkDownloadFiles(ids, { fallbackFilename = 'docket-contracts.zip' } = {}) {
  const response = await apiFetch('/contracts/bulk/download', { json: { ids } });
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  if (!response.ok) {
    const body = isJson ? await response.json().catch(() => null) : null;
    throw new ApiError(body?.error || body?.message || `Request failed (HTTP ${response.status})`, {
      status: response.status,
      body,
    });
  }
  const headerIds = (response.headers.get('x-docket-skipped-ids') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (isJson) {
    // Nothing downloadable — every selected contract lacked a document.
    const body = await response.json();
    const bodyIds = (body.skipped || []).map((s) => s.id);
    return { downloaded: 0, skippedIds: bodyIds.length ? bodyIds : headerIds };
  }
  saveBlobAsFile(await response.blob(), downloadFilename(response, fallbackFilename));
  return { downloaded: Math.max(ids.length - headerIds.length, 0), skippedIds: headerIds };
}
